import { LicensePlan } from '../../types';
import { 
  baseOfficeFeatures, 
  baseSecurityFeatures, 
  baseCollaborationFeatures 
} from '../features/commonFeatures';
import { frontlineSpecificFeatures } from '../features/frontlineFeatures';
import { mergeFeatures, combineFeatureSets } from '../../utils/licenseUtils';

const f1BaseFeatures = {
  'Exchange Online': '2 GB mailbox',
  'SharePoint Online': '2 GB per user',
  'Teams': false,
  'Web versions of Office': true,
  'OneDrive for Business': '2 GB storage',
  'Microsoft Lists': true,
  'Microsoft Stream': true,
  'Microsoft Forms': true,
  'Basic Security': true,
  'Microsoft Entra ID P1': true,
  'Microsoft Intune': true,
  'Shifts': true,
  'Tasks': true,
  'Walkie Talkie': true,
  'Updates': true,
  'Staff Scheduling': 'Basic',
  'Power Apps for Frontline': 'Limited use rights',
  'Power Automate for Frontline': 'Limited use rights',
  'Copilot Chat enhancements': 'Inbox/calendar awareness + Word, Excel, PowerPoint agents',
  'Copilot Chat Analytics': true,
};

const f1WithTeamsFeatures = {
  ...f1BaseFeatures,
  'Teams': true,
};

const f3BaseFeatures = {
  ...f1BaseFeatures,
  'Exchange Online': '50 GB mailbox',
  'SharePoint Online': '10 GB per user + 2 GB per license',
  'OneDrive for Business': '5 GB storage',
  'Staff Scheduling': 'Advanced',
  'Virtual Appointments': true,
  'Approvals': true,
  'Microsoft Defender for Office 365 P1': true,
  'Power Apps for Frontline': 'Full use rights',
  'Power Automate for Frontline': 'Full use rights',
};

const f3WithTeamsFeatures = {
  ...f3BaseFeatures,
  'Teams': true,
};

const buildFrontlineFeatures = (overrides: Record<string, boolean | string>) =>
  combineFeatureSets(
    mergeFeatures(baseOfficeFeatures, overrides),
    mergeFeatures(baseSecurityFeatures, overrides),
    mergeFeatures(baseCollaborationFeatures, overrides),
    mergeFeatures(frontlineSpecificFeatures, overrides)
  );

export const m365FrontlinePlans: LicensePlan[] = [
  {
    id: 'm365-f1',
    name: 'Microsoft 365 F1 (no Teams)',
    description: 'Essential tools for frontline workers with limited functionality (Teams sold separately)',
    pricing: {
      points: [
        { amount: 1.75, currency: 'USD', unit: 'user/month', effective: '1970-01-01' },
        { amount: 2.5, currency: 'USD', unit: 'user/month', effective: '2026-07-01' }
      ]
    },
    features: buildFrontlineFeatures(f1BaseFeatures)
  },
  {
    id: 'm365-f1-with-teams',
    name: 'Microsoft 365 F1 with Teams',
    description: 'Essential tools for frontline workers with Teams included',
    pricing: {
      points: [
        { amount: 2.25, currency: 'USD', unit: 'user/month', effective: '1970-01-01' },
        { amount: 3.0, currency: 'USD', unit: 'user/month', effective: '2026-07-01' }
      ]
    },
    features: buildFrontlineFeatures(f1WithTeamsFeatures)
  },
  {
    id: 'm365-f3',
    name: 'Microsoft 365 F3 (no Teams)',
    description: 'Purpose-built for frontline workers with full mobile capabilities (Teams sold separately)',
    pricing: {
      points: [
        { amount: 6.93, currency: 'USD', unit: 'user/month', effective: '1970-01-01' },
        { amount: 8.93, currency: 'USD', unit: 'user/month', effective: '2026-07-01' }
      ]
    },
    features: buildFrontlineFeatures(f3BaseFeatures)
  },
  {
    id: 'm365-f3-with-teams',
    name: 'Microsoft 365 F3 with Teams',
    description: 'Purpose-built for frontline workers with Teams included',
    pricing: {
      points: [
        { amount: 8.0, currency: 'USD', unit: 'user/month', effective: '1970-01-01' },
        { amount: 10.0, currency: 'USD', unit: 'user/month', effective: '2026-07-01' }
      ]
    },
    features: buildFrontlineFeatures(f3WithTeamsFeatures)
  }
];
